// src/myWorkspace/listviewWorkspace.js — the user's workspaces as a thumbnail grid, below the uploader on /workspace

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './listviewWorkspace.css';

// FIREBASE
import { collection, onSnapshot, setDoc } from 'firebase/firestore';
import { storage, db, auth } from '../firebase';
import { ref, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';

// IMAGES
import placeholder_color_thumbnail from '../assets/images/thumbnail/no-content-bumpups.jpg';

// FONT AWESOME
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrash,
  faMagnifyingGlass,
  faXmark,
  faCircleNotch,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';

const getLocalVideoCacheKey = userId => `videoCache_${userId}_WorkspaceVideos`;

// Cards rendered per "Show more" step — the full list is already loaded, this just caps the DOM
const CARDS_PER_STEP = 24;

const titleOf = video => video.workspace_title || video.video_file_name || 'Untitled workspace';

// The grid's fields only. Whole workspace docs can carry pre-chat answers (output_map), which made the
// cache big enough to hit the storage quota and silently stop caching.
const CARD_FIELDS = [
  'id', 'workspace_title', 'video_file_name', 'video_thumbnail', 'video_duration_in_seconds', 'workspace_creation_time',
  'is_transcript_error', 'is_transcript_available', 'is_local_transcript_available',
  'is_caption_processing', 'is_local_caption_processing', 'is_example', 'preview_locked', 'origin',
];
const toCard = data => Object.fromEntries(CARD_FIELDS.filter(k => data[k] !== undefined).map(k => [k, data[k]]));

// 'active' is the normal state and gets no badge; everything else is called out on the card
const statusOf = video => {
  if (video.is_transcript_error) return 'error';
  if (video.is_transcript_available || video.is_local_transcript_available) return 'active';
  if (video.is_caption_processing || video.is_local_caption_processing) return 'processing';
  return 'draft';
};

const formatRelativeTime = ts => {
  if (!ts?.seconds) return '';
  const date = new Date(ts.seconds * 1000);
  const secondsAgo = (Date.now() - date.getTime()) / 1000;
  if (secondsAgo < 60) return 'Just now';
  if (secondsAgo < 3600) return `${Math.floor(secondsAgo / 60)}m ago`;
  if (secondsAgo < 86400) return `${Math.floor(secondsAgo / 3600)}h ago`;
  if (secondsAgo < 172800) return 'Yesterday';
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
};

const formatDuration = sec => {
  if (sec == null || isNaN(sec)) return null;
  const t = Math.floor(sec),
    h = Math.floor(t / 3600),
    m = Math.floor((t % 3600) / 60),
    s = t % 60;
  return h
    ? `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
    : `${m}:${s.toString().padStart(2, '0')}`;
};

// onCounts({ total, own }): how many workspaces there are, and how many aren't ready-made examples.
// hideEmpty: the page shows its own getting-started panel instead of the empty state.
const ListViewWorkspace = ({ holdUpdates = false, onCounts, hideEmpty = false }) => {
  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [query, setQuery] = useState('');
  const [shownCount, setShownCount] = useState(CARDS_PER_STEP);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();

  // FUNCTIONS
  const functions = getFunctions();
  const deleteAssetsCallable = httpsCallable(functions, 'deleteAssets');

  // While a workspace is being created above, keep the current cards on screen so the new one
  // doesn't flash in right before the redirect into it; catch up once the hold is released.
  const holdUpdatesRef = useRef(holdUpdates);
  holdUpdatesRef.current = holdUpdates;
  const latestVideosRef = useRef(null);
  useEffect(() => {
    if (!holdUpdates && latestVideosRef.current) setVideos(latestVideosRef.current);
  }, [holdUpdates]);

  const checkFirestore = useCallback(userId => {
    const localVideoCacheKey = getLocalVideoCacheKey(userId);

    const userVideosRef = collection(db, 'users', userId, 'workspaceContent');

    const unsubscribeWorkspaceContent = onSnapshot(
      userVideosRef,
      async snapshot => {
        if (!snapshot.empty) {
          const videosFromFirestore = [];
          const thumbnailPromises = [];
          snapshot.forEach(docSnap => {
            const data = toCard({ ...docSnap.data(), id: docSnap.id.replace('workspace-', '') });
            if (data.video_thumbnail?.startsWith('bumpThumbnails/')) {
              thumbnailPromises.push(
                (async () => {
                  try {
                    const url = await getDownloadURL(
                      ref(storage, data.video_thumbnail)
                    );
                    await setDoc(docSnap.ref, { video_thumbnail: url }, { merge: true });
                    data.video_thumbnail = url;
                  } catch {
                    data.video_thumbnail = placeholder_color_thumbnail;
                  }
                })()
              );
            }
            videosFromFirestore.push(data);
          });
          await Promise.all(thumbnailPromises);
          videosFromFirestore.sort(
            (a, b) =>
              (b.workspace_creation_time?.seconds ?? 0) -
              (a.workspace_creation_time?.seconds ?? 0)
          );
          latestVideosRef.current = videosFromFirestore;
          if (!holdUpdatesRef.current) setVideos(videosFromFirestore);
          try {
            localStorage.setItem(localVideoCacheKey, JSON.stringify(videosFromFirestore));
          } catch (e) {
            if (e.name.includes('Quota')) {
              localStorage.removeItem(localVideoCacheKey);
            }
          }
        } else {
          latestVideosRef.current = [];
          if (!holdUpdatesRef.current) setVideos([]);
          localStorage.removeItem(localVideoCacheKey);
        }
        setIsLoading(false);
      },
      () => setIsLoading(false)
    );

    return unsubscribeWorkspaceContent;
  }, []);

  useEffect(() => {
    setIsLoading(true);
    const user = auth.currentUser;
    if (!user) {
      setIsLoading(false);
      return;
    }
    const cacheKey = getLocalVideoCacheKey(user.uid);
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      setVideos(JSON.parse(cached));
      setIsLoading(false);
    }

    const unsub = checkFirestore(user.uid);
    return () => unsub && unsub();
  }, [checkFirestore]);

  useEffect(() => {
    if (isLoading) return;
    onCounts?.({ total: videos.length, own: videos.filter(v => !v.is_example).length });
  }, [isLoading, videos, onCounts]);

  // ── Search & selection ─────────────────────────────────────────────────────
  const normalizedQuery = query.trim().toLowerCase();
  const visibleVideos = normalizedQuery
    ? videos.filter(v => titleOf(v).toLowerCase().includes(normalizedQuery))
    : videos;
  const shownVideos = visibleVideos.slice(0, shownCount);
  const hiddenCount = visibleVideos.length - shownVideos.length;

  // A new search starts again from the top of its results
  useEffect(() => {
    setShownCount(CARDS_PER_STEP);
  }, [normalizedQuery]);

  const isSelecting = selectedIds.size > 0;

  const toggleSelected = id => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  // Shift+click selects a run of cards: from the last card clicked (the anchor) to this one, in on-screen
  // order. The run takes the anchor's state, so it can deselect too (like Gmail / Photos).
  const anchorRef = useRef(null);
  const selectCard = (id, shift) => {
    const ids = shownVideos.map(v => v.id);
    const from = anchorRef.current ? ids.indexOf(anchorRef.current) : -1;
    const to = ids.indexOf(id);
    if (shift && from !== -1 && to !== -1) {
      const anchor = anchorRef.current;
      const run = ids.slice(Math.min(from, to), Math.max(from, to) + 1);
      setSelectedIds(prev => {
        const next = new Set(prev);
        const select = prev.has(anchor);
        run.forEach(r => (select ? next.add(r) : next.delete(r)));
        return next;
      });
    } else {
      toggleSelected(id);
    }
    anchorRef.current = id;
  };
  useEffect(() => {
    if (!isSelecting) anchorRef.current = null;
  }, [isSelecting]);

  // Only what's on screen — never select (and bulk-delete) cards the user hasn't seen
  const selectAllVisible = () => setSelectedIds(new Set(shownVideos.map(v => v.id)));
  const clearSelection = () => setSelectedIds(new Set());

  // Escape leaves selection mode
  useEffect(() => {
    if (!isSelecting) return;
    const onKeyDown = e => { if (e.key === 'Escape') clearSelection(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isSelecting]);

  const openWorkspace = id => navigate(`/workspace/${id}`);

  // In selection mode a click toggles the card (like Photos/Drive); otherwise it opens it.
  // Shift+click always selects, so it can also start a selection.
  const handleCardActivate = (id, shift) => (isSelecting || shift ? selectCard(id, shift) : openWorkspace(id));

  // ── Delete ─────────────────────────────────────────────────────────────────
  const confirmDelete = ids => setPendingDeleteIds(ids);
  const onCancelDeletion = () => setPendingDeleteIds([]);
  const onConfirmDeletion = () => {
    const ids = pendingDeleteIds;
    setPendingDeleteIds([]);
    handleDelete(ids);
  };

  const handleDelete = async ids => {
    setIsDeleting(true);
    const assetsList = ids.map(id => `workspace-${id}`);
    try {
      await deleteAssetsCallable({ assetsList });
      const user = auth.currentUser;
      if (user) {
        const cacheKey = getLocalVideoCacheKey(user.uid);
        const raw = localStorage.getItem(cacheKey);
        if (raw) {
          const filtered = JSON.parse(raw).filter(vid => !ids.includes(vid.id));
          localStorage.setItem(cacheKey, JSON.stringify(filtered));
        }
      }
      setVideos(prev => prev.filter(v => !ids.includes(v.id)));
      setSelectedIds(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.delete(id));
        return next;
      });
    } catch (err) {
      console.error('Error deleting assets:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const renderStatus = status => {
    if (status === 'processing') return (
      <span className="listworkspace-status listworkspace-status--processing">
        <FontAwesomeIcon icon={faCircleNotch} spin /> Processing
      </span>
    );
    if (status === 'error') return (
      <span className="listworkspace-status listworkspace-status--error">
        <FontAwesomeIcon icon={faTriangleExclamation} /> Failed
      </span>
    );
    if (status === 'draft') return (
      <span className="listworkspace-status listworkspace-status--draft">Draft</span>
    );
    return null;
  };

  const deleteCount = pendingDeleteIds.length;

  return (
    <div className="listworkspace-video-container">
      <div className="listworkspace-toolbar">
        {isSelecting ? (
          <>
            <span className="listworkspace-selected-count">{selectedIds.size} selected</span>
            <div className="listworkspace-toolbar-actions">
              <button type="button" className="listworkspace-text-btn" onClick={selectAllVisible}>Select all</button>
              <button type="button" className="listworkspace-text-btn" onClick={clearSelection}>Cancel</button>
              <button
                type="button"
                className="listworkspace-bulk-delete"
                onClick={() => confirmDelete([...selectedIds])}
              >
                <FontAwesomeIcon icon={faTrash} /> Delete
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="listworkspace-heading">
              Your workspaces
              {videos.length > 0 && <span className="listworkspace-count">{videos.length}</span>}
            </h2>
            {videos.length > 0 && (
              <div className="listworkspace-search">
                <FontAwesomeIcon icon={faMagnifyingGlass} className="listworkspace-search-icon" />
                <input
                  type="text"
                  className="listworkspace-search-input"
                  placeholder="Search workspaces"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  aria-label="Search workspaces"
                />
                {query && (
                  <button type="button" className="listworkspace-search-clear" onClick={() => setQuery('')} aria-label="Clear search">
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {isLoading ? (
        <div className="listworkspace-loading">
          <div className="listworkspace-loading-spinner" />
          Loading workspaces…
        </div>
      ) : videos.length === 0 ? (hideEmpty ? null : (
        <div className="listworkspace-empty">
          <div className="listworkspace-empty-title">No workspaces yet</div>
          <div className="listworkspace-empty-sub">
            Paste a YouTube link or drop a video above to create your first one.
          </div>
        </div>
      )) : visibleVideos.length === 0 ? (
        <div className="listworkspace-empty">
          <div className="listworkspace-empty-title">No matches</div>
          <div className="listworkspace-empty-sub">Nothing called “{query.trim()}” yet.</div>
        </div>
      ) : (
        <div className={`listworkspace-grid${isSelecting ? ' is-selecting' : ''}`}>
          {shownVideos.map(video => {
            const status = statusOf(video);
            const title = titleOf(video);
            const duration = formatDuration(video.video_duration_in_seconds);
            const isSelected = selectedIds.has(video.id);
            return (
              <div
                key={video.id}
                className={`listworkspace-card${isSelected ? ' is-selected' : ''}`}
                role="link"
                tabIndex={0}
                onClick={e => handleCardActivate(video.id, e.shiftKey)}
                onKeyDown={e => { if (e.key === 'Enter') handleCardActivate(video.id, e.shiftKey); }}
                onMouseDown={e => { if (e.shiftKey) e.preventDefault(); }} // no text highlight on Shift+click
              >
                <div className="listworkspace-thumb">
                  <img
                    src={video.video_thumbnail || placeholder_color_thumbnail}
                    alt=""
                    loading="lazy"
                    className="listworkspace-thumbnail"
                    onError={e => { e.currentTarget.src = placeholder_color_thumbnail; }}
                  />
                  {renderStatus(status)}
                  {duration && <span className="listworkspace-duration">{duration}</span>}
                  {status === 'processing' && <div className="listworkspace-progress" />}

                  <label className="listworkspace-check" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={e => selectCard(video.id, e.nativeEvent.shiftKey)}
                      aria-label={`Select ${title}`}
                    />
                  </label>
                  <button
                    type="button"
                    className="listworkspace-card-delete"
                    aria-label={`Delete ${title}`}
                    title="Delete"
                    onClick={e => { e.stopPropagation(); confirmDelete([video.id]); }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
                <div className="listworkspace-card-title" title={title}>{title}</div>
                <div className="listworkspace-card-meta">
                  {video.is_example && <span className="listworkspace-tag">Example</span>}
                  {video.origin === 'api' && <span className="listworkspace-tag">API</span>}
                  {video.preview_locked && <span className="listworkspace-tag listworkspace-tag--preview">Free preview</span>}
                  {formatRelativeTime(video.workspace_creation_time)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!isLoading && hiddenCount > 0 && (
        <div className="listworkspace-show-more-row">
          <button
            type="button"
            className="listworkspace-show-more"
            onClick={() => setShownCount(c => c + CARDS_PER_STEP)}
          >
            Show more
          </button>
          <span className="listworkspace-show-more-count">
            {shownVideos.length} of {visibleVideos.length}
          </span>
        </div>
      )}

      {deleteCount > 0 && (
        <div className="d-popup-overlay">
          <div className="d-popup-content">
            <h2>Delete {deleteCount === 1 ? 'this workspace' : `${deleteCount} workspaces`}?</h2>
            <p>
              This action is irreversible and the workspace won’t be recoverable,
              so all outputs will be lost.
            </p>
            <div className="d-popup-buttons">
              <button className="d-popup-cancel" onClick={onCancelDeletion}>
                Cancel
              </button>
              <button className="d-popup-confirm" onClick={onConfirmDeletion}>
                Confirm Deletion
              </button>
            </div>
          </div>
        </div>
      )}

      {isDeleting && (
        <div className="bulk-delete-overlay">
          <div className="bulk-delete-dialog">
            <div className="bulk-delete-spinner" />
            <div className="bulk-delete-text">Deleting…</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListViewWorkspace;
